import type {
  SpeechOutService,
  SpeechOutputRequest,
  SpeechVoiceDescriptor,
} from "../speech-out";
import { describeVoices, type DeviceVoice } from "./voices";

export interface SpeechUtterance {
  onend: (() => void) | null;
  onerror: ((event: { readonly error?: string }) => void) | null;
  onstart: (() => void) | null;
  rate: number;
  voice: DeviceVoice | null;
}

export interface SpeechSynthesisAdapter {
  cancel(): void;
  getVoices(): readonly DeviceVoice[];
  pause(): void;
  resume(): void;
  speak(utterance: SpeechUtterance): void;
}

export interface IntervalTimer {
  clearInterval(handle: number): void;
  clearTimeout(handle: number): void;
  setInterval(callback: () => void, delayMs: number): number;
  setTimeout(callback: () => void, delayMs: number): number;
}

/**
 * Chrome and Brave are both known to drop an utterance's end event. Without a
 * cap the speak promise never settles, the control that awaits it never
 * re-enables, and nothing says why. Every await on a browser speech event is
 * therefore bounded.
 *
 * The allowance scales with the text, because a calibration passage takes far
 * longer than a chunk, plus a fixed margin for the engine to start.
 */
export const SPEECH_START_GRACE_MS = 8_000;
export const SPEECH_MS_PER_CHARACTER = 140;

export function speechTimeoutMs(text: string, rate: number): number {
  const safeRate = rate > 0 ? rate : 1;
  return Math.ceil(
    SPEECH_START_GRACE_MS + (text.length * SPEECH_MS_PER_CHARACTER) / safeRate,
  );
}

export interface VisibilitySource {
  readonly hidden: boolean;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

export interface BrowserSpeechOutService extends SpeechOutService {
  dispose(): void;
  requiresVisibleResume(): boolean;
}

export function createBrowserSpeechOutService(
  synthesis: SpeechSynthesisAdapter,
  createUtterance: (text: string) => SpeechUtterance,
  timer: IntervalTimer,
  visibility?: VisibilitySource,
): BrowserSpeechOutService {
  let keepalive: number | undefined;
  let resumeRequired = false;
  let watchdog: number | undefined;
  const pendingCancellations = new Set<() => void>();

  const stopKeepalive = (): void => {
    if (keepalive === undefined) return;
    timer.clearInterval(keepalive);
    keepalive = undefined;
  };
  const stopWatchdog = (): void => {
    if (watchdog === undefined) return;
    timer.clearTimeout(watchdog);
    watchdog = undefined;
  };
  const visibilityChanged = (): void => {
    if (!visibility?.hidden) return;
    synthesis.pause();
    resumeRequired = true;
  };
  visibility?.addEventListener("visibilitychange", visibilityChanged);

  return {
    voices: () => describeVoices(synthesis.getVoices()),
    cancel: () => {
      stopKeepalive();
      stopWatchdog();
      synthesis.cancel();
      for (const settle of Array.from(pendingCancellations)) settle();
      return undefined;
    },
    pause: () => {
      synthesis.pause();
      return undefined;
    },
    resume: () => {
      resumeRequired = false;
      synthesis.resume();
      return undefined;
    },
    requiresVisibleResume: () => resumeRequired,
    dispose: () => {
      stopKeepalive();
      stopWatchdog();
      synthesis.cancel();
      for (const settle of Array.from(pendingCancellations)) settle();
      visibility?.removeEventListener("visibilitychange", visibilityChanged);
    },
    speak: (request: SpeechOutputRequest) =>
      new Promise<void>((resolve, reject) => {
        let settled = false;
        const finish = (failure?: Error): void => {
          if (settled) return;
          settled = true;
          pendingCancellations.delete(cancelled);
          stopKeepalive();
          stopWatchdog();
          if (failure === undefined) resolve();
          else reject(failure);
        };
        const cancelled = (): void => finish();
        pendingCancellations.add(cancelled);
        const voices = synthesis.getVoices();
        const selected =
          request.voiceId === undefined
            ? null
            : (voices.find(({ voiceURI }) => voiceURI === request.voiceId) ??
              null);
        if (request.voiceId !== undefined && selected === null) {
          finish(
            new Error(
              `Selected speech voice is unavailable: ${request.voiceId}`,
            ),
          );
          return;
        }
        if (selected !== null && !selected.localService) {
          finish(new Error("The selected voice requires the network."));
          return;
        }
        const utterance = createUtterance(request.text);
        utterance.voice = selected;
        utterance.rate = request.rate ?? 1;
        utterance.onstart = () => {
          request.onStart?.();
          stopKeepalive();
          keepalive = timer.setInterval(() => synthesis.resume(), 10_000);
        };
        utterance.onend = () => {
          finish();
        };
        utterance.onerror = (event) => {
          finish(
            new Error(
              `Speech synthesis failed: ${event.error ?? "unknown error"}`,
            ),
          );
        };
        // Bounded await. If the engine never reports start, end or error the
        // watchdog cancels it and the failure is reported, never silent.
        stopWatchdog();
        watchdog = timer.setTimeout(
          () => {
            synthesis.cancel();
            finish(
              new Error(
                // Named every browser's problem as Brave's until 12 October
                // 2026, when the trainee hit it in Firefox and was told to
                // check Shields, which Firefox does not have.
                "This voice did not start speaking, so its rate could not be measured. Try another local voice. On Brave, also check that Shields is not blocking this site.",
              ),
            );
          },
          speechTimeoutMs(request.text, request.rate ?? 1),
        );
        synthesis.speak(utterance);
      }),
  };
}

export function browserVoiceById(
  voices: readonly SpeechVoiceDescriptor[],
  voiceId: string,
): SpeechVoiceDescriptor | null {
  return voices.find(({ id }) => id === voiceId) ?? null;
}
